import React from "react";
import { Easing, interpolate, random, spring, useCurrentFrame } from "remotion";
import { theme } from "../../brand";
import { LOOK } from "../../b2c/look";
import { Tap } from "../../b2c/ui/bits";
import { FrakLogo } from "../../components/FrakLogo";
import { REEL, ReelFrame } from "../ui/ReelFrame";
import { Bat, Coins, Ghost, NIGHT, Pumpkin, Sparkle, Wand } from "./art";
import { HALLOWEEN } from "./copy";

/**
 * Reel "Halloween" (9:16, silent, ~13.8s): a haunted bank screen full of
 * everyday purchases melts, falls apart, and the Frak logo puts it back
 * together and fills it.
 *   1. Cover (frame 0 is complete, no line of text): the night bank
 *      screen, tall and centred, already sagging and dripping; a huge
 *      balance dropping fast; five purchase rows (name left, red price
 *      right) that flash and throw their price out one after another;
 *      flicker and shake. Pumpkins, ghosts and bats around it.
 *   2. Disintegration: a glitch, then the screen breaks into a grid of
 *      tiles that detach from the top-left corner, spin, drift up like
 *      ash and fade. The balance reads 0 €. A dark empty frame for a beat.
 *   3. Rescue: the "Partager et Gagner" button springs in, rests readable
 *      for about a second, is tapped and held pressed. Flash and a light
 *      burst push the night away (pumpkins and ghosts go with it), the
 *      Frak logo swoops in from the top with an overshoot and docks above
 *      the screen under "Frak remplit ton compte bancaire", between two
 *      magic wands. The same tiles fly back and snap into place, clean
 *      and solid; the balance counts up from 0 € to 150 € while the rows
 *      light up green, with sparkles. Then the usual Frak CTA.
 *
 * The balance and the prices are illustrative (a drawn screen); no
 * reward is shown. Text and button stay in the Reels-safe band (y
 * 250–1580). Everything is deterministic (`random(seed)`), 110 tiles, and
 * the tiles share one memoised screen, so stills and server renders match
 * and stay light.
 */

/** Timeline, in frames. Holds are reading time: never shorten one. */
const T = {
  /** The balance is 0 and the screen breaks (glitch from `break - 8`). */
  break: 66,
  /**
   * The button, on screen ~2 s: springs in with a glow (`button`, ~0.5 s),
   * sits still and readable (until `tap`, ~1 s), is tapped and held
   * pressed (~0.4 s), then the rescue lands.
   */
  button: 130,
  tap: 174,
  rescue: 190,
  /** The logo leaves the centre for its place above the screen. */
  dock: 208,
  /** The tiles fly back; all are snapped before `solid`. */
  reform: 214,
  solid: 254,
  /** The balance counts up; a credit row lights up on each `credits`. */
  count: 256,
  countLen: 36,
  credits: [260, 267, 274, 281, 288],
  exit: 322,
  end: 332,
  duration: 414,
} as const;

export const HALLOWEEN_DURATION = T.duration;

/**
 * Illustrative balances, in €. The opening one is about the sum of the
 * purchases in `copy.ts` (621 €); Frak fills the account up to `refill`,
 * and never above.
 */
const BAL = { start: 640, refill: 150 } as const;
/** The five rows, then the extra debits flying out with them. */
const PILLS = [...HALLOWEEN.items, ...HALLOWEEN.extras];
const debit = (price: number) => `−${price}\u00A0€`;
const NONE = [0, 0, 0, 0, 0] as const;

const CX = REEL.width / 2;
const RED = LOOK.red;
const ORANGE = theme.brand.colors.warning[600];
const MINT = theme.brand.colors.success[300];
const GREEN = theme.brand.colors.success;
const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const ramp = (frame: number, at: number, len: number, ease = Easing.out(Easing.cubic)) =>
  interpolate(frame, [at, at + len], [0, 1], { ...clamp, easing: ease });
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const mix = (a: string, b: string, t: number) =>
  `color-mix(in srgb, ${a} ${Math.round((1 - t) * 100)}%, ${b})`;
const eur = (n: number) =>
  `${Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0")}\u00A0€`;

/** The screen (logical px, 1080×1920), cut in COLS×ROWS tiles. */
const COLS = 10;
const ROWS = 11;
const TW = 86;
const TH = 84;
/** Clean screen: under the logo, ends at 1564 (Reels-safe band: 1580). */
const SCREEN = { left: 110, top: 640, width: COLS * TW, height: ROWS * TH } as const;
/** The night screen starts at y 320: tall and centred, no void above it. */
const NIGHT_DY = -320;
/** Rows inside the screen: 96 px tall, 110 px pitch. */
const ROW_H = 96;
const ROW_TOPS = [346, 456, 566, 676, 786] as const;

const L = {
  relief: { top: 252, height: 230, size: 84 },
  button: { top: 1400, left: 110, width: 860, height: 150 },
} as const;
const BUTTON_Y = L.button.top + L.button.height / 2;
const HERO = { w: 560, h: 230, logo: 380, cy: 1000, dockY: 556, dockScale: 0.5 } as const;

/** Melt progress: already sagging on the cover, fully melted at the break. */
const meltAt = (frame: number) =>
  lerp(0.6, 1, ramp(frame, 0, T.break - 4, Easing.inOut(Easing.quad)));

/** Drain progress 0..1: slow start, then faster and faster. */
const drain = (frame: number) =>
  Math.pow(Math.min(1, Math.max(0, (frame + 30) / (T.break - 6 + 30))), 1.7);

const balanceAt = (frame: number) =>
  frame < T.break
    ? lerp(BAL.start, 0, drain(frame))
    : frame < T.count
      ? 0
      : lerp(0, BAL.refill, ramp(frame, T.count, T.countLen));

/** Constant tremble while it melts, then impacts (decaying jitter). */
const IMPACTS = [
  { at: T.break, amp: 26, len: 40 },
  { at: T.rescue, amp: 14, len: 12 },
] as const;
const shake = (frame: number) => {
  const live = frame < T.break ? 2 + 8 * drain(frame) : 0;
  let x = (random(`sx${frame}`) - 0.5) * 2 * live;
  let y = (random(`sy${frame}`) - 0.5) * 2 * live * 0.7;
  for (const { at, amp, len } of IMPACTS) {
    const t = frame - at;
    if (t < 0 || t > len) continue;
    const k = amp * (1 - t / len);
    x += (random(`ix${at}${frame}`) - 0.5) * 2 * k;
    y += (random(`iy${at}${frame}`) - 0.5) * 2 * k * 0.7;
  }
  return { x, y };
};

/** The night: gradient, stars, fog, bats. Pushed away by the logo's light. */
const Night: React.FC = () => {
  const frame = useCurrentFrame();
  if (frame >= T.rescue + 32) return null;
  const reveal = ramp(frame, T.rescue, 30) * 2000;
  const mask =
    frame >= T.rescue
      ? `radial-gradient(circle at 50% ${HERO.cy}px, transparent ${reveal}px, #000 ${reveal + 220}px)`
      : undefined;
  return (
    <div
      style={{
        position: "absolute",
        // Bleeds past the frame so the shake never shows the blue behind.
        inset: -40,
        maskImage: mask,
        WebkitMaskImage: mask,
        background: `radial-gradient(120% 80% at 50% 20%, ${NIGHT.violet} 0%, ${NIGHT.deep} 72%)`,
      }}
    >
      {Array.from({ length: 28 }, (_, i) => {
        const r = 2 + random(`sr${i}`) * 3;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: random(`sx${i}`) * REEL.width,
              top: random(`sy${i}`) * 820,
              width: r * 2,
              height: r * 2,
              borderRadius: r,
              background: LOOK.sky,
              opacity: 0.35 + 0.5 * Math.abs(Math.sin(frame / 8 + random(`sp${i}`) * 6)),
            }}
          />
        );
      })}
      {[640, 1010, 1330].map((y, i) => (
        <div
          key={y}
          style={{
            position: "absolute",
            left: -160,
            top: y,
            width: 1400,
            height: 360,
            background:
              "radial-gradient(closest-side, rgba(168,150,255,0.22), rgba(168,150,255,0))",
            translate: `${Math.sin(frame / 60 + i * 2) * 90}px 0`,
          }}
        />
      ))}
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: ((frame * (5 + i * 2) + i * 460) % 1500) - 240,
            top: [150, 900, 1180][i] + Math.sin((frame + i * 20) / 9) * 30,
          }}
        >
          <Bat size={110} flap={Math.abs(Math.sin(frame / 2.2 + i))} />
        </div>
      ))}
    </div>
  );
};

/** The relief line, once the tiles have mostly landed. */
const Headline: React.FC = () => {
  const frame = useCurrentFrame();
  // After the tiles have mostly landed, so none flies behind the line.
  const reliefAt = T.reform + 22;
  const inn = spring({
    frame: frame - reliefAt,
    fps: REEL.fps,
    config: { damping: 11, stiffness: 190, mass: 0.7 },
  });
  if (frame < reliefAt) return null;
  return (
    <div
      style={{
        position: "absolute",
        top: L.relief.top,
        left: 110,
        width: 860,
        height: L.relief.height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          color: LOOK.white,
          textAlign: "center",
          lineHeight: 1.02,
          fontSize: L.relief.size,
          letterSpacing: -L.relief.size * 0.035,
          scale: inn,
          opacity: inn,
        }}
      >
        <div>{HALLOWEEN.relief[0]}</div>
        <div style={{ color: MINT }}>{HALLOWEEN.relief[1]}</div>
      </div>
    </div>
  );
};

type ScreenProps = {
  /** Night (false) or clean Frak look (true). */
  light: boolean;
  melt: number;
  balance: number;
  /** Per row: credit progress (clean) and debit flash (night). */
  credit: readonly number[];
  hit: readonly number[];
  /** Frame of the wobble (frozen inside the tiles). */
  t: number;
};

/**
 * The bank screen: label, a huge balance, a gauge and five rows (the
 * purchases at night, credit lines once clean). One memoised copy per
 * tile: its props don't change within a phase.
 */
const Screen = React.memo<ScreenProps>(({ light, melt, balance, credit, hit, t }) => {
  const wob = Math.sin(t / 5);
  const gauge = Math.min(1, Math.max(0.03, balance / (light ? BAL.refill : BAL.start)));
  const tone = Math.min(1, balance / (BAL.refill * 0.3));
  const muted = light ? LOOK.inkSoft : LOOK.blueSoft;
  return (
    <div
      style={{
        position: "relative",
        width: SCREEN.width,
        height: SCREEN.height,
        boxSizing: "border-box",
        borderRadius: 44,
        background: light ? LOOK.white : NIGHT.card,
        border: `${light ? 5 : 3}px solid ${light ? LOOK.blue : NIGHT.line}`,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 44,
          top: 28,
          fontFamily: LOOK.body,
          fontWeight: LOOK.semi,
          fontSize: 40,
          color: muted,
        }}
      >
        {HALLOWEEN.balance}
      </div>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 76,
          height: 206,
          textAlign: "center",
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: 200,
          lineHeight: "206px",
          letterSpacing: -8,
          fontVariantNumeric: "tabular-nums",
          color: light
            ? mix(LOOK.ink, GREEN[700], tone)
            : theme.brand.colors.error[400],
          transformOrigin: "50% 25%",
          scale: `1 ${1 + 0.32 * melt + 0.03 * melt * wob}`,
        }}
      >
        {eur(balance)}
      </div>
      <div
        style={{
          position: "absolute",
          left: 40,
          right: 40,
          top: 306,
          height: 22,
          borderRadius: 11,
          background: light ? LOOK.hairline : NIGHT.line,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${gauge * 100}%`,
            height: "100%",
            borderRadius: 11,
            background: light ? GREEN[600] : RED,
          }}
        />
      </div>
      {ROW_TOPS.map((top, i) => {
        const c = Math.min(1, Math.max(0, credit[i] ?? 0));
        const h = hit[i] ?? 0;
        const item = HALLOWEEN.items[i];
        const sag = melt * (4 + i * 8) + wob * melt * 3;
        const low = 30 + melt * 22;
        return (
          <div
            key={top}
            style={{
              position: "absolute",
              left: 36,
              right: 36,
              top,
              height: ROW_H,
              overflow: "hidden",
              borderRadius: `30px 30px ${low}px ${low}px`,
              background: light ? LOOK.mist : mix(NIGHT.row, RED, h * 0.5),
              transformOrigin: "50% 0",
              translate: `0 ${sag}px`,
              scale: `1 ${1 + 0.18 * melt}`,
            }}
          >
            {/* Credit: green sweeps in from the left. */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                width: `${c * 100}%`,
                background: GREEN[100],
              }}
            />
            <div
              style={{
                position: "absolute",
                left: 24,
                top: 16,
                width: 64,
                height: 64,
                borderRadius: 32,
                background: light ? mix(LOOK.sky, GREEN[600], c) : RED,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {light ? (
                <div style={{ scale: credit[i] ?? 0, opacity: c }}>
                  <Coins size={40} />
                </div>
              ) : (
                <svg width="30" height="30" viewBox="0 0 24 24">
                  <path
                    d="M6 9l6 7 6-7"
                    fill="none"
                    stroke={LOOK.white}
                    strokeWidth={3.6}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            {light ? (
              <>
                <div
                  style={{
                    position: "absolute",
                    left: 112,
                    top: 26,
                    width: 300,
                    height: 20,
                    borderRadius: 10,
                    background: mix(LOOK.hairline, GREEN[300], c),
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: 112,
                    top: 58,
                    width: 190,
                    height: 14,
                    borderRadius: 7,
                    background: mix(LOOK.hairline, GREEN[200], c),
                    opacity: 0.7,
                  }}
                />
                <svg
                  width="44"
                  height="44"
                  viewBox="0 0 24 24"
                  style={{ position: "absolute", right: 32, top: 26, opacity: c }}
                >
                  <path
                    d="M6 15l6-7 6 7"
                    fill="none"
                    stroke={GREEN[700]}
                    strokeWidth={3.6}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </>
            ) : (
              <div
                style={{
                  position: "absolute",
                  left: 112,
                  right: 32,
                  top: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontFamily: LOOK.display,
                  fontWeight: LOOK.bold,
                  fontSize: 46,
                  letterSpacing: -1,
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ color: LOOK.white }}>{item.name}</span>
                <span style={{ color: theme.brand.colors.error[400] }}>{debit(item.price)}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});
Screen.displayName = "Screen";

/** The screen's tiles: seeded scatter, shared by the break and the reform. */
const TILES = Array.from({ length: COLS * ROWS }, (_, i) => ({
  col: i % COLS,
  row: Math.floor(i / COLS),
  a: random(`ta${i}`),
  b: random(`tb${i}`),
  c: random(`tc${i}`),
}));
const FLY_OUT = 34;
const FLY_BACK = 22;

/**
 * Tiles flying apart (night) or back together (clean). Out: from the
 * top-left corner, accelerating, spinning, drifting up and right like
 * ash, fading. Back: from the bottom-right, same scatter in reverse, with
 * a little overshoot so each one snaps in.
 */
const Tiles: React.FC<{ light: boolean }> = ({ light }) => {
  const frame = useCurrentFrame();
  const t = light ? T.reform : T.break;
  return (
    <>
      {TILES.map(({ col, row, a, b, c }) => {
        let k: number;
        let opacity: number;
        let size: number;
        if (light) {
          const delay = (COLS - 1 - col + (ROWS - 1 - row)) * 0.7 + b * 4;
          const local = frame - (T.reform + delay);
          const q = interpolate(local, [0, FLY_BACK], [0, 1], {
            ...clamp,
            easing: Easing.out(Easing.back(1.3)),
          });
          const start = local > 0;
          k = 1 - q;
          opacity = start ? Math.min(1, q * 4) : 0;
          size = lerp(0.4, 1, Math.min(1, q));
        } else {
          const delay = (col + row) * 0.9 + a * 5;
          const p = interpolate(frame - (T.break + delay), [0, FLY_OUT], [0, 1], {
            ...clamp,
            easing: Easing.in(Easing.quad),
          });
          if (p >= 1) return null;
          k = p;
          opacity = interpolate(p, [0, 0.45, 1], [1, 0.9, 0]);
          size = 1 - 0.6 * p;
        }
        return (
          <div
            key={`${col}-${row}`}
            style={{
              position: "absolute",
              left: col * TW,
              top: row * TH,
              // 1px overlap: no hairline seams between tiles at rest.
              width: TW + 1,
              height: TH + 1,
              overflow: "hidden",
              translate: `${(a - 0.35) * 560 * k}px ${-(60 + b * 360) * k}px`,
              rotate: `${(c - 0.5) * 520 * k}deg`,
              scale: size,
              opacity,
            }}
          >
            <div style={{ position: "absolute", left: -col * TW, top: -row * TH }}>
              <Screen light={light} melt={light ? 0 : 1} balance={0} credit={NONE} hit={NONE} t={t} />
            </div>
          </div>
        );
      })}
    </>
  );
};

const DRIPS = Array.from({ length: 7 }, (_, i) => ({
  x: SCREEN.left + 80 + i * 116 + (random(`dx${i}`) - 0.5) * 40,
  w: 36 + random(`dw${i}`) * 26,
  len: 70 + random(`dl${i}`) * 60,
  lag: random(`dg${i}`) * 0.3,
}));

/** Drips hanging under the melting card; they fall away at the break. */
const Drips: React.FC = () => {
  const frame = useCurrentFrame();
  if (frame >= T.break + 12) return null;
  const m = meltAt(frame);
  const fall = ramp(frame, T.break, 12, Easing.in(Easing.quad));
  const attach = SCREEN.top + NIGHT_DY + SCREEN.height * (1 + 0.07 * m) - 14;
  return (
    <>
      {DRIPS.map((d, i) => {
        const grow = Math.min(1, Math.max(0, (m - 0.2 - d.lag) / 0.8));
        const len = d.len * grow + Math.sin(frame / 7 + i) * 6 * grow;
        const bulb = d.w * 1.25;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: d.x,
              top: attach,
              translate: `0 ${fall * 420}px`,
              opacity: 1 - fall,
            }}
          >
            <div
              style={{
                position: "absolute",
                left: -d.w * 0.3,
                top: 0,
                width: d.w * 0.6,
                height: len,
                background: NIGHT.drip,
              }}
            />
            <div
              style={{
                position: "absolute",
                left: -bulb / 2,
                top: len - bulb * 0.2,
                width: bulb,
                height: bulb * 1.15,
                borderRadius: "50% 50% 50% 50% / 40% 40% 60% 60%",
                background: NIGHT.drip,
              }}
            />
          </div>
        );
      })}
    </>
  );
};

/** SVG displacement used only while the card melts (wavy, sagging edges). */
const MeltDefs: React.FC<{ m: number }> = ({ m }) => {
  const frame = useCurrentFrame();
  return (
    <svg width="0" height="0" style={{ position: "absolute" }}>
      <filter id="halloween-melt" x="-15%" y="-15%" width="130%" height="140%">
        <feTurbulence
          type="fractalNoise"
          baseFrequency={`0.005 ${0.018 + 0.004 * Math.sin(frame / 14)}`}
          numOctaves={1}
          seed={3}
          result="n"
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="n"
          scale={8 + 24 * m}
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
    </svg>
  );
};

/** A purchase is thrown out (and its row flashes) on this frame, one after another. */
const fireAt = (i: number) => -20 + i * 6;
const hitAt = (frame: number, i: number) => {
  const t = frame - fireAt(i);
  return t >= 0 && t < 10 ? Math.sin((Math.PI * t) / 10) : 0;
};

/** The card: solid while it melts, tiles while it breaks and re-forms. */
const CardLayer: React.FC = () => {
  const frame = useCurrentFrame();
  const night = frame < T.reform;
  const melting = frame < T.break;
  const tiles = night ? frame >= T.break : frame < T.solid;
  const m = melting ? meltAt(frame) : 1;
  const glitch = frame >= T.break - 8 && frame < T.break + 26;
  const jump = glitch && random(`gj${frame}`) > 0.7 ? (random(`gx${frame}`) - 0.5) * 60 : 0;
  const blink = glitch && frame < T.break && random(`gb${frame}`) > 0.75 ? 0.55 : 1;
  const clean = frame >= T.solid;
  const credits = clean
    ? T.credits.map((at) =>
        spring({ frame: frame - at, fps: REEL.fps, config: { damping: 10, stiffness: 200, mass: 0.6 } }),
      )
    : NONE;
  const hits = melting ? ROW_TOPS.map((_, i) => hitAt(frame, i)) : NONE;
  return (
    <>
      {melting ? <MeltDefs m={m} /> : null}
      <div
        style={{
          position: "absolute",
          left: SCREEN.left,
          top: SCREEN.top + (night ? NIGHT_DY : 0),
          width: SCREEN.width,
          height: SCREEN.height,
          transformOrigin: "50% 0",
          transform: night
            ? `translateX(${jump}px) skewX(${Math.sin(frame / 5) * 2.2 * m}deg) scaleY(${1 + 0.07 * m})`
            : undefined,
          filter: melting ? "url(#halloween-melt)" : undefined,
          opacity: blink,
        }}
      >
        {clean || melting ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 44,
              boxShadow: clean
                ? "0 40px 80px -20px rgba(0, 40, 143, 0.55)"
                : "0 0 60px rgba(120,100,255,0.25), 0 40px 80px -20px rgba(0,0,0,0.55)",
              opacity: clean ? ramp(frame, T.solid, 8) : 1,
            }}
          />
        ) : null}
        {tiles ? (
          <Tiles light={!night} />
        ) : (
          <Screen
            light={clean}
            melt={clean ? 0 : m}
            balance={balanceAt(frame)}
            credit={credits}
            hit={hits}
            t={frame}
          />
        )}
      </div>
    </>
  );
};

/** Glitch bars across the screen around the break. */
const Glitch: React.FC = () => {
  const frame = useCurrentFrame();
  if (frame < T.break - 8 || frame >= T.break + 26) return null;
  const colors = [LOOK.white, RED, LOOK.blueSoft];
  return (
    <>
      {[0, 1, 2, 3, 4].map((i) => {
        if (random(`gv${frame}${i}`) < 0.5) return null;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: SCREEN.left - 40,
              width: SCREEN.width + 80,
              top: SCREEN.top + NIGHT_DY + random(`gy${frame}${i}`) * SCREEN.height,
              height: 6 + random(`gh${frame}${i}`) * 26,
              background: colors[i % colors.length],
              opacity: 0.5,
            }}
          />
        );
      })}
    </>
  );
};

/**
 * Red purchases flying out of the card, one after another. In front: the
 * price of each row leaves it (price only, so no row text is covered).
 * Behind the card: the extra purchases, with their name, emerging at the
 * sides.
 */
const Debits: React.FC<{ front: boolean }> = ({ front }) => {
  const frame = useCurrentFrame();
  const top = SCREEN.top + NIGHT_DY;
  return (
    <>
      {PILLS.map((p, i) => {
        if ((i < ROW_TOPS.length) !== front) return null;
        const t = frame - fireAt(i);
        if (t < 0 || t > 34) return null;
        const side = front ? 1 : i % 2 ? 1 : -1;
        const k = t / 34;
        const y = i < ROW_TOPS.length ? top + ROW_TOPS[i] + 48 : top + 160 + ((i * 149) % 700);
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: CX + side * ((front ? 337 : 250) + 780 * Math.pow(k, 1.6)),
              top: y + 50 * k,
              translate: "-50% -50%",
              rotate: `${side * 30 * k}deg`,
              padding: "10px 26px",
              borderRadius: 999,
              background: RED,
              color: LOOK.white,
              fontFamily: LOOK.display,
              fontWeight: LOOK.bold,
              fontSize: 44,
              whiteSpace: "nowrap",
              boxShadow: "0 14px 30px -8px rgba(0,0,0,0.5)",
              opacity: interpolate(t, [0, 3, 28, 34], [0, 1, 1, 0], clamp),
            }}
          >
            {front ? debit(p.price) : `${p.name} ${debit(p.price)}`}
          </div>
        );
      })}
    </>
  );
};

/** Ghosts behind the card's edges; they close in, then poof at the rescue. */
const GHOSTS = [
  { x: -10, y: 520, size: 190, dir: 1 },
  { x: 890, y: 800, size: 200, dir: -1 },
  { x: 20, y: 1130, size: 120, dir: 1 },
  { x: 940, y: 420, size: 120, dir: -1 },
] as const;
const Ghosts: React.FC = () => {
  const frame = useCurrentFrame();
  const vanish = ramp(frame, T.rescue, 12, Easing.in(Easing.cubic));
  if (vanish >= 1) return null;
  const closing = ramp(frame, T.break, 50);
  return (
    <>
      {GHOSTS.map((g, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: g.x,
            top: g.y,
            opacity: 1 - vanish,
            scale: (0.95 + 0.25 * closing) * (1 - vanish),
            rotate: `${Math.sin(frame / 10 + i) * 5 + vanish * 40 * g.dir}deg`,
            translate: `${g.dir * 150 * closing}px ${
              Math.sin(frame / 9 + i * 1.7) * 16 - vanish * 220
            }px`,
          }}
        >
          <Ghost size={g.size} />
        </div>
      ))}
    </>
  );
};

/** Light burst of the rescue: rotating rays and two rings. Under the text. */
const Rescue: React.FC = () => {
  const frame = useCurrentFrame() - T.rescue;
  if (frame < 0 || frame > 34) return null;
  const rays = interpolate(frame, [0, 12], [0, 1], clamp);
  const fade = 1 - ramp(frame, 8, 20, Easing.in(Easing.quad));
  const radial = "radial-gradient(closest-side, #000 0%, transparent 72%)";
  return (
    <>
      <div
        style={{
          position: "absolute",
          left: CX - 1300,
          top: HERO.cy - 1300,
          width: 2600,
          height: 2600,
          background:
            "repeating-conic-gradient(from 0deg, rgba(255,255,255,0.3) 0deg 7deg, rgba(255,255,255,0) 7deg 20deg)",
          maskImage: radial,
          WebkitMaskImage: radial,
          rotate: `${frame * 1.4}deg`,
          scale: rays,
          opacity: fade,
        }}
      />
      {[0, 7].map((delay) => {
        const t = ramp(frame - delay, 0, 22);
        return (
          <div
            key={delay}
            style={{
              position: "absolute",
              left: CX - 200,
              top: HERO.cy - 200,
              width: 400,
              height: 400,
              borderRadius: 400,
              border: `10px solid ${LOOK.white}`,
              boxSizing: "border-box",
              scale: t * 4.2,
              opacity: (1 - t) * 0.9,
            }}
          />
        );
      })}
    </>
  );
};

/** Soft light behind the logo: strong on arrival, gone before the text. */
const HeroGlow: React.FC = () => {
  const frame = useCurrentFrame();
  if (frame < T.rescue || frame > T.dock + 24) return null;
  const dock = spring({
    frame: frame - T.dock,
    fps: REEL.fps,
    config: { damping: 16, stiffness: 170, mass: 0.8 },
  });
  const y = lerp(HERO.cy, HERO.dockY, dock);
  return (
    <div
      style={{
        position: "absolute",
        left: CX - 600,
        top: y - 600,
        width: 1200,
        height: 1200,
        background:
          "radial-gradient(closest-side, rgba(255,255,255,0.85), rgba(120,170,255,0.35) 45%, rgba(0,67,239,0))",
        opacity: ramp(frame, T.rescue, 8) * (1 - ramp(frame, T.dock, 18)),
        scale: lerp(1, 0.6, dock),
      }}
    />
  );
};

/**
 * The Frak logo, the hero: swoops in from the top with an overshoot,
 * lands in the middle of the empty frame, then docks above the screen.
 * The real logo component, brand blue, on a white tile for contrast.
 */
const HeroLogo: React.FC = () => {
  const frame = useCurrentFrame();
  if (frame < T.rescue) return null;
  const land = spring({
    frame: frame - T.rescue,
    fps: REEL.fps,
    config: { damping: 9, stiffness: 110, mass: 0.9 },
  });
  const dock = spring({
    frame: frame - T.dock,
    fps: REEL.fps,
    config: { damping: 16, stiffness: 170, mass: 0.8 },
  });
  const y = lerp(lerp(-200, HERO.cy, land), HERO.dockY, dock);
  const pulse = T.credits.reduce(
    (sum, at) => sum + 0.07 * Math.sin(Math.PI * ramp(frame, at, 12)),
    0,
  );
  const s = lerp(0.45 + 0.55 * land, HERO.dockScale, dock) * (1 + pulse);
  return (
    <div
      style={{
        position: "absolute",
        left: CX - HERO.w / 2,
        top: y - HERO.h / 2,
        width: HERO.w,
        height: HERO.h,
        borderRadius: 64,
        background: LOOK.white,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow:
          "0 0 110px 24px rgba(255,255,255,0.45), 0 40px 80px -20px rgba(0, 40, 143, 0.6)",
        scale: s,
        rotate: `${(1 - land) * -8}deg`,
      }}
    >
      <FrakLogo width={HERO.logo} />
    </div>
  );
};

/** Sparkles and coins popping from a row's icon when it is credited. */
const Sparks: React.FC = () => {
  const frame = useCurrentFrame();
  const colors = [GREEN[500], ORANGE, LOOK.blue];
  return (
    <>
      {T.credits.map((at, row) => {
        const t = frame - at;
        if (t < 0 || t > 34) return null;
        const k = Math.min(1, t / 26);
        const out = 1 - (1 - k) * (1 - k);
        return Array.from({ length: 7 }, (_, i) => {
          const angle = (i / 7) * 2 * Math.PI + random(`ka${row}${i}`);
          const power = 90 + random(`kp${row}${i}`) * 110;
          const size = 26 + random(`ks${row}${i}`) * 20;
          return (
            <div
              key={`${row}-${i}`}
              style={{
                position: "absolute",
                left: SCREEN.left + 92 - size / 2,
                top: SCREEN.top + ROW_TOPS[row] + ROW_H / 2 - size / 2,
                translate: `${Math.cos(angle) * power * out}px ${Math.sin(angle) * power * out}px`,
                scale: 1 - 0.4 * k,
                opacity: interpolate(t, [18, 34], [1, 0], clamp),
              }}
            >
              <Sparkle size={size} color={colors[i % colors.length]} />
            </div>
          );
        });
      })}
    </>
  );
};

/** Pumpkins of the dark part: they poof away when the logo arrives. */
const Pumpkins: React.FC = () => {
  const frame = useCurrentFrame();
  const vanish = ramp(frame, T.rescue, 12, Easing.in(Easing.cubic));
  if (vanish >= 1) return null;
  return (
    <>
      {[
        { size: 230, left: -20, top: 1330, rot: -6 },
        { size: 210, left: 880, top: 1345, rot: 7 },
      ].map((p, i) => (
        <Pumpkin
          key={i}
          size={p.size}
          style={{
            position: "absolute",
            left: p.left,
            top: p.top,
            rotate: `${p.rot + vanish * 50 * (i ? 1 : -1)}deg`,
            scale: 1 - vanish,
            opacity: 1 - vanish,
          }}
        />
      ))}
    </>
  );
};

/** Magic wand of the Frak part: a flick every ~1.1 s, a star burst, a light trail. */
const WAND = { size: 270, top: 475, inset: 30 } as const;
const MagicWand: React.FC<{ side: 1 | -1; at: number }> = ({ side, at }) => {
  const frame = useCurrentFrame();
  const t = frame - at;
  const inn = spring({
    frame: t,
    fps: REEL.fps,
    config: { damping: 10, stiffness: 170, mass: 0.7 },
  });
  if (t < 0) return null;
  const cyc = t % 34;
  const flick = cyc < 14 ? Math.sin((Math.PI * cyc) / 14) : 0;
  const rot = -24 * flick + Math.sin(t / 9) * 4;
  const burst = cyc >= 9 && cyc < 29 ? (cyc - 9) / 20 : -1;
  const star =
    1 + 0.15 * Math.sin(t / 4) + (burst >= 0 ? 0.5 * Math.sin(Math.PI * Math.min(1, burst * 2)) : 0);
  const px = WAND.size / 200;
  const gold = theme.brand.colors.warning[300];
  return (
    <div
      style={{
        position: "absolute",
        left: side === 1 ? WAND.inset : REEL.width - WAND.inset - WAND.size,
        top: WAND.top,
        width: WAND.size,
        height: WAND.size,
        transformOrigin: "15% 85%",
        transform: `scale(${side * inn}, ${inn}) rotate(${rot}deg)`,
      }}
    >
      <Wand size={WAND.size} star={star} trail={flick} />
      {burst >= 0
        ? Array.from({ length: 7 }, (_, i) => {
            const angle = (i / 7) * 2 * Math.PI + random(`wb${side}${i}`);
            const reach = (50 + random(`wr${side}${i}`) * 50) * burst;
            const size = 22 + random(`wz${side}${i}`) * 16;
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: 150 * px + Math.cos(angle) * reach - size / 2,
                  top: 50 * px + Math.sin(angle) * reach - size / 2,
                  opacity: 1 - burst,
                  scale: 1 - 0.4 * burst,
                }}
              >
                <Sparkle size={size} color={i % 2 ? LOOK.white : gold} />
              </div>
            );
          })
        : null}
    </div>
  );
};

const Wands: React.FC = () => (
  <>
    <MagicWand side={1} at={T.dock + 6} />
    <MagicWand side={-1} at={T.dock + 11} />
  </>
);

/**
 * The Frak widget's button (pill, brand blue, coins), the rescue's
 * trigger: springs in with a glow, rests readable, is pressed on the tap.
 */
const Button: React.FC = () => {
  const frame = useCurrentFrame();
  if (frame < T.button || frame > T.rescue + 10) return null;
  const pop = spring({
    frame: frame - T.button,
    fps: REEL.fps,
    config: { damping: 10, stiffness: 170, mass: 0.8 },
  });
  // Pressed at the tap, held ~0.4 s, released as the rescue lands.
  const press = interpolate(
    frame,
    [T.tap, T.tap + 4, T.tap + 14, T.tap + 18],
    [1, 0.9, 0.9, 1],
    clamp,
  );
  const gone = ramp(frame, T.rescue, 8, Easing.in(Easing.quad));
  // Entry glow, then a slow pulse while it waits; a halo on the press.
  const entry = 1 - ramp(frame, T.button, 16);
  const wait = frame < T.tap ? 0.5 + 0.5 * Math.sin((frame - T.button) / 5) : 0;
  const halo = 12 + 70 * entry + 14 * wait + (frame >= T.tap ? 30 : 0);
  return (
    <div
      style={{
        position: "absolute",
        left: L.button.left,
        top: L.button.top,
        width: L.button.width,
        height: L.button.height,
        borderRadius: L.button.height,
        background: LOOK.blue,
        border: `8px solid ${LOOK.white}`,
        boxSizing: "border-box",
        boxShadow: `0 0 ${halo}px ${halo / 3}px rgba(255,255,255,${0.35 + 0.25 * entry}), 0 0 0 ${10 + 8 * wait}px rgba(255,255,255,0.18), 0 30px 60px -16px rgba(0, 40, 143, 0.7)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 26,
        color: LOOK.white,
        fontFamily: LOOK.body,
        fontWeight: LOOK.semi,
        fontSize: 64,
        whiteSpace: "nowrap",
        translate: `0 ${(1 - pop) * 260}px`,
        scale: pop * press * (1 - 0.4 * gone),
        opacity: Math.min(1, pop * 2) * (1 - gone),
      }}
    >
      {HALLOWEEN.button}
      <Coins size={78} />
    </div>
  );
};

export const HalloweenReel: React.FC = () => {
  const frame = useCurrentFrame();
  const out = ramp(frame, T.exit, 10, Easing.in(Easing.quad));
  const s = shake(frame);
  const flash = (() => {
    const r = frame - T.break;
    if (r >= 0 && r < 8) return { color: RED, a: 0.35 * (1 - r / 8) };
    const k = frame - (T.tap + 3);
    if (k >= 0 && k < 6) return { color: LOOK.white, a: 0.16 * (1 - k / 6) };
    const w = frame - T.rescue;
    if (w >= 0 && w < 12) return { color: LOOK.white, a: 0.7 * (1 - w / 12) };
    return { color: LOOK.white, a: 0 };
  })();
  const blink = frame > 5 && frame < T.break + 16 && random(`bl${frame}`) > 0.9 ? 0.35 : 0;

  return (
    <ReelFrame
      exitAt={T.exit}
      end={{ at: T.end, line: HALLOWEEN.end.line, sub: HALLOWEEN.end.sub }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 1 - out,
          scale: 1 - 0.05 * out,
          translate: `${s.x}px ${s.y}px`,
        }}
      >
        <Night />
        <div style={{ position: "absolute", inset: 0, background: "#000", opacity: blink }} />
        {/* Flash and light: under every text, so they never wash one out. */}
        <div
          style={{ position: "absolute", inset: 0, background: flash.color, opacity: flash.a }}
        />
        <Rescue />
        <HeroGlow />

        {/* Dark part only: pumpkins and ghosts. The Frak part has wands. */}
        <Pumpkins />
        <Ghosts />
        <Drips />
        <Debits front={false} />
        <CardLayer />
        <Glitch />
        <Sparks />
        <Debits front />
        <Wands />
        <Headline />
        <HeroLogo />
        <Button />
        <Tap x={L.button.left + L.button.width - 130} y={BUTTON_Y} at={T.tap} size={120} />
      </div>
    </ReelFrame>
  );
};
